import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Flex, Text } from '@chakra-ui/react';
import {
  NOTIFICATION_TYPE,
  NOTIFICATION_TYPE_TOPIC_MAP,
  PLATFORM,
  SUBSCRIPTION_STATUS_TYPE,
} from '../../../../../constants/benchat';
import { useAsyncSubscription } from '../../../../../hooks';
import pnotifyService from '../../../../../service/pnotifyService';
import benchatService from '../../../../../service/benchatService';
import trackerService from '../../../../../service/trackerService';
import {
  EVENT_NAME_FOR_SUBSCRIPTION,
  EVENT_CATEGORY_FOR_SUBSCRIPTION,
  EVENT_PROPERTY_FOR_SUBSCRIPTION,
  EVENT_PROPERTY_VALUE_FOR_SUBSCRIPTION,
} from '../../../../../shop/constants/tracker';
import { useAppContext } from '../../../context/appContext';
import {
  FacebookSubscriptionButton,
  FacebookSubscriptionDialog,
} from '../../../../../component/FacebookSubscription';

const FacebookSubscription = (props) => {
  const { e2eId, subscriptions, notificationType, shouldCheckMemberReward } =
    props;
  const { t } = useTranslation();
  const { currentUser, facebookSubscriptionConfig } = useAppContext();
  const userId = currentUser._id;
  const {
    channel: { platformChannelId },
  } = facebookSubscriptionConfig;
  const {
    hasSubscribed,
    startPolling,
    subscriptionStatus,
    unsubscribe,
    userRef,
    checkMemberRewardForLINEOrFB,
  } = useAsyncSubscription({
    userId,
    platform: PLATFORM.FACEBOOK,
    topics: NOTIFICATION_TYPE_TOPIC_MAP[notificationType],
    subscriptions,
  });
  const isLoading = subscriptionStatus === SUBSCRIPTION_STATUS_TYPE.LOADING;

  useEffect(() => {
    switch (subscriptionStatus) {
      case SUBSCRIPTION_STATUS_TYPE.SUCCESS:
        if (shouldCheckMemberReward) checkMemberRewardForLINEOrFB(userId);
        pnotifyService.notify(t('member_profile.subscribe.successfully'));
        break;
      case SUBSCRIPTION_STATUS_TYPE.SOCIAL_USER_DOUBLE_BIND_ERROR:
        pnotifyService.notify(
          t('member_profile.social.subscribe.error.already_bound_to'),
          {
            customClass: 'error',
            icon: 'fa fa-exclamation-triangle',
          },
        );
        break;
      case SUBSCRIPTION_STATUS_TYPE.SOCIAL_USER_BIND_ERROR:
        pnotifyService.notify(t('member_profile.subscribe.error'), {
          customClass: 'error',
          icon: 'fa fa-exclamation-triangle',
        });
        break;
      case SUBSCRIPTION_STATUS_TYPE.UNKNOWN_ERROR:
        pnotifyService.notify(t('member_profile.facebook.subscribe.error'), {
          customClass: 'error',
          icon: 'fa fa-exclamation-triangle',
        });
        break;
      case SUBSCRIPTION_STATUS_TYPE.NO_RECORD:
      default:
        break;
    }
  }, [
    subscriptionStatus,
    t,
    checkMemberRewardForLINEOrFB,
    userId,
    shouldCheckMemberReward,
  ]);

  const handleUnsubscribe = async () => {
    const result = await unsubscribe();
    if (result === SUBSCRIPTION_STATUS_TYPE.SUCCESS) {
      pnotifyService.notify(t('member_profile.unsubscribe.successfully'));
    } else {
      pnotifyService.notify(t('member_profile.unsubscribe.error'), {
        customClass: 'error',
        icon: 'fa fa-exclamation-triangle',
      });
    }
  };

  const [isOpen, setIsOpen] = useState(false);
  const [focusFlag, setFocusFlag] = useState(false);

  const onOpen = useCallback(() => {
    setIsOpen(true);

    trackerService.track({
      type: EVENT_NAME_FOR_SUBSCRIPTION.MEMBER_SUBSCRIPTION_BUTTON_CLICK,
      data: {
        event_category: EVENT_CATEGORY_FOR_SUBSCRIPTION.MEMBER_SUBSCRIPTION,
        [EVENT_PROPERTY_FOR_SUBSCRIPTION.SUBSCRIPTION_TYPE]:
          notificationType === NOTIFICATION_TYPE.MARKETING
            ? EVENT_PROPERTY_VALUE_FOR_SUBSCRIPTION.PROMOTION_NOTIFICATION
            : EVENT_PROPERTY_VALUE_FOR_SUBSCRIPTION.ORDER_NOTIFICATION,
        [EVENT_PROPERTY_FOR_SUBSCRIPTION.SUBSCRIPTION_PATH]:
          EVENT_PROPERTY_VALUE_FOR_SUBSCRIPTION.MEMBER_CENTER,
        [EVENT_PROPERTY_FOR_SUBSCRIPTION.THIRD_PARTY]:
          EVENT_PROPERTY_VALUE_FOR_SUBSCRIPTION.FACEBOOK,
      },
    });
  }, [notificationType]);
  const onClose = useCallback(() => {
    setIsOpen(false);
  }, []);
  const onConfirm = useCallback(() => {
    const url = benchatService.getMDotMeLink(
      platformChannelId,
      `${userRef.current}-${notificationType}`,
    );
    window.open(url);

    trackerService.track({
      type: EVENT_NAME_FOR_SUBSCRIPTION.MEMBER_SUBSCRIPTION_POPUP_CLICK,
      data: {
        event_category: EVENT_CATEGORY_FOR_SUBSCRIPTION.MEMBER_SUBSCRIPTION,
        [EVENT_PROPERTY_FOR_SUBSCRIPTION.THIRD_PARTY]:
          EVENT_PROPERTY_VALUE_FOR_SUBSCRIPTION.FACEBOOK,
      },
    });

    setFocusFlag(true);
    setIsOpen(false);
  }, [platformChannelId, userRef, notificationType]);

  useEffect(() => {
    if (!focusFlag) return;
    const onWindowFocus = () => {
      startPolling();
      setFocusFlag(false);
    };

    window.addEventListener('focus', onWindowFocus);

    return () => {
      window.removeEventListener('focus', onWindowFocus);
    };
  }, [focusFlag, startPolling]);

  return (
    <Flex justifyContent="space-between" alignItems="center">
      <Text
        variant="formLabel"
        className="member-center-profile__label"
        textAlign="left"
      >
        Facebook
      </Text>
      <FacebookSubscriptionButton
        className="facebook-subscription-button_hover-border"
        isLoading={isLoading}
        isSubscribed={hasSubscribed}
        onClick={hasSubscribed ? handleUnsubscribe : onOpen}
        e2eId={e2eId}
      >
        {hasSubscribed
          ? t('member_profile.subscribed')
          : t('member_profile.subscribe')}
      </FacebookSubscriptionButton>
      <FacebookSubscriptionDialog
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={onConfirm}
      />
    </Flex>
  );
};

FacebookSubscription.propTypes = {
  subscriptions: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
    }),
  ),
  notificationType: PropTypes.string.isRequired,
  e2eId: PropTypes.string,
  shouldCheckMemberReward: PropTypes.bool,
};

export default FacebookSubscription;
